import { combineEpics } from "redux-observable";
import { from, of } from "rxjs";
import { catchError, filter, mergeMap, switchMap } from "rxjs/operators";

import { Action, actionCreators, isOfAction } from "../actions";
import { getFlattenRoomViews, RoomType } from "../reducers/rooms";
import { findRoomIdByUri } from "../reducers/roster";
import { Epic, ExtraArguments, RootStateObservable } from "../utils/types";

const findRoomIdByUriPromise = async (
    state$: RootStateObservable,
    uri: string,
    { App }: ExtraArguments
) => {
    const roomId = findRoomIdByUri(state$.value, uri);
    if (roomId) {
        return roomId;
    } else {
        let data;
        try {
            data = await App.proto.getRoster({ sip_uris: [uri] }, { tag: "[incoming_call_sip]" });
        } catch (e) {
            console.error("Failed to get roster by sip uri for incoming call", e);
        }
        if (data && data.rooms && data.rooms[0]) {
            App.actionCreators.onRoster({ data });
            return data.rooms[0].id;
        }
    }
    return;
};

const openOrActivateRoom = (roomId: string, state$: RootStateObservable): Action[] => {
    const roomViews = getFlattenRoomViews(state$.value);
    const roomView = roomViews.find(x => x.roomId === roomId && x.type === RoomType.REAL_TIME);
    if (!roomView) {
        return [actionCreators.setRoomToOpen({ roomId, type: RoomType.REAL_TIME })];
    } else {
        return [actionCreators.setRoomViewAsActive({ roomView, focus: true })];
    }
};

const findAndOpenRoom = (sipUri: string, state$: RootStateObservable, extra: ExtraArguments) =>
    from(findRoomIdByUriPromise(state$, sipUri, extra)).pipe(
        mergeMap((roomId): Action[] => {
            if (roomId) {
                return openOrActivateRoom(roomId, state$);
            } else {
                return [
                    actionCreators.startCallFailedToFindRoomId({
                        phoneNumber: sipUri,
                    }),
                ];
            }
        })
    );

// watchers

const onSMSPressed: Epic = (action$, state$, extra) =>
    action$.pipe(
        filter(isOfAction(actionCreators.dialpadSMSPressed)),
        switchMap(({ payload: sipUri }) =>
            findAndOpenRoom(sipUri, state$, extra).pipe(
                catchError(err => {
                    console.error("Failed to get roomId for dialpad number");
                    return of(
                        actionCreators.startCallFailedToFindRoomId({
                            phoneNumber: sipUri,
                        })
                    );
                })
            )
        )
    );

export const sipCallEpic = combineEpics(onSMSPressed);
